import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, ShoppingBag } from 'lucide-react';
import type { Product } from '@/types/product';
import config from '@/config';

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { t, i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';
  const [imgError, setImgError] = useState(false);

  const name = isHindi ? product.nameHi : product.name;
  const description = isHindi ? product.descriptionHi : product.description;

  const whatsappMessage = `Hi! I'm interested in: ${product.name} (${product.priceDisplay}). Please share details.`;
  const whatsappUrl = `https://wa.me/${config.contact.whatsapp}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border border-gray-300 bg-white shadow transition-shadow hover:shadow-lg">
      {/* Image */}
      <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-blue-50">
        {imgError ? (
          <div className="flex flex-col items-center gap-3 text-blue-400">
            <ShoppingBag size={48} />
            <span className="text-sm font-semibold text-blue-500">{name.split(' ').slice(0, 2).join(' ')}</span>
          </div>
        ) : (
          <img
            src={product.image}
            alt={name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            onError={() => setImgError(true)}
          />
        )}
        {!product.inStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <span className="rounded-full bg-red-500 px-3 py-1 text-sm font-medium text-white">
              {t('products.outOfStock')}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-semibold text-gray-900 line-clamp-2">{name}</h3>
        <p className="mt-1 text-sm text-gray-500 line-clamp-2">{description}</p>

        <div className="mt-auto flex items-center justify-between pt-3">
          <span className="text-lg font-bold text-primary">{product.priceDisplay}</span>
          {product.inStock && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-lg bg-green-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-600"
            >
              <MessageCircle size={14} />
              {t('products.orderWhatsApp')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
